/**
 * Who may be signed in on the server-hosted app: the server's owner and the
 * accounts it is shared with, nobody else. A rago.tv session alone says
 * nothing about this server, so before one counts here two parties must
 * agree: the broker issues a server-scoped token for this server (it has a
 * live grant for the account) and the server accepts it (GET /v1/me, its own
 * grants table). The sign-in runs the check on a session it has not adopted
 * yet (lib/linkFlow); a session already in — a share revoked since — is
 * signed out on the same verdict (lib/localResources). This is the page's
 * manners, not the boundary: the server refuses every request without a
 * grant whatever the page believes.
 */
import type { LocalMe } from "@rago/web-shared";
import { auth } from "./authManager";
import { noGrant, requestCloudToken, type StoredToken } from "./localSession";

export type AccessVerdict =
  | { kind: "admitted"; token: StoredToken; me: LocalMe }
  /** The broker has no grant for the account, or the server refused it. */
  | { kind: "denied" }
  /** The cloud or the server did not answer: nothing is known. */
  | { kind: "unverified" };

const ME_TIMEOUT_MS = 8_000;

/** The server-side half, as lib/localResources reads it: the principal, or
 * the status of a refusal. Throws when the server does not answer. */
export async function fetchLocalMe(origin: string, token: string): Promise<LocalMe | number> {
  const res = await fetch(`${origin}/v1/me`, {
    headers: { authorization: `Bearer ${token}` },
    cache: "no-store",
    signal: AbortSignal.timeout(ME_TIMEOUT_MS),
  });
  if (!res.ok) return res.status;
  return (await res.json()) as LocalMe;
}

export async function verifyServerAccess(serverId: string, bearer: string): Promise<AccessVerdict> {
  let token: StoredToken;
  try {
    token = await requestCloudToken(serverId, bearer);
  } catch (err) {
    return { kind: noGrant(err) ? "denied" : "unverified" };
  }
  try {
    const me = await fetchLocalMe(window.location.origin, token.token);
    if (typeof me !== "number") return { kind: "admitted", token, me };
    // 403: the server's own table says revoked. Anything else (401 clock
    // skew or a key it does not know yet, 503) is no verdict on the account.
    return { kind: me === 403 ? "denied" : "unverified" };
  } catch {
    return { kind: "unverified" };
  }
}

/** Why the sign-in page is showing, when the app sent the user there. */
export type SignInNotice = "no_access";

// Outside the `rago.local.` prefix on purpose: sign-out clears that one.
const NOTICE_KEY = "rago.signin.notice";

export function readSignInNotice(): SignInNotice | null {
  try {
    return sessionStorage.getItem(NOTICE_KEY) === "no_access" ? "no_access" : null;
  } catch {
    return null;
  }
}

export function clearSignInNotice(): void {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // no sessionStorage
  }
}

let leaving: Promise<void> | null = null;

/** The signed-in account has no access to this server (any more): sign out
 * — the broker session is revoked, every stored token goes — and the
 * sign-in page, where the layout sends a signed-out tab, says why.
 * Single-flight: every request of a page load reaches the same verdict. */
export function loseAccess(): Promise<void> {
  leaving ??= (async () => {
    try {
      sessionStorage.setItem(NOTICE_KEY, "no_access");
    } catch {
      // no sessionStorage: the plain sign-in page
    }
    await auth.signOut();
  })().finally(() => {
    leaving = null;
  });
  return leaving;
}
